'use client'

import { useCallback } from 'react'
import { format } from 'date-fns'
import {
  useCreateJournal,
  useCreateTask,
  useCreateMovie,
  useCreateBook,
  useCreateArtist,
  useCreateLabel,
  useCreateRelease,
} from '@/entries'
import type { CaptureEntryType } from '../types'
import type { UnifiedDraftValues } from './useFormState'
import type { JournalAnalysis, TaskAnalysis } from '../analysis/types'
import type { MediaKind } from '@/entries'
import type { MediaEditValues } from '../media-preview/types'
import type { EnrichedMediaResult } from '@/enrichment'
/** Minimal person interface - only needs id for submission */
type PersonRef = { id: string }
import {
  buildMediaCreateData,
  type MediaAnalysis,
} from '../ingest/from-analysis'

export interface SubmissionCallbacks {
  onSuccess?: () => void
  onResetAll?: () => void
}

export interface UseFormSubmissionReturn {
  isLoading: boolean
  submitJournalOrTask: (
    draft: UnifiedDraftValues,
    effectiveType: CaptureEntryType | null,
    selectedPeople: PersonRef[],
    analysis: JournalAnalysis | TaskAnalysis | null,
    callbacks: SubmissionCallbacks,
  ) => Promise<void>
  submitMedia: (
    aiDetectedType: MediaKind,
    analysis: MediaAnalysis,
    enrichedData: EnrichedMediaResult | null,
    editedMediaValues: MediaEditValues | undefined,
    callbacks: SubmissionCallbacks,
  ) => Promise<void>
  submitManualMedia: (
    dismissedMediaType: MediaKind,
    content: string,
    editedMediaValues: MediaEditValues | undefined,
    callbacks: SubmissionCallbacks,
  ) => Promise<void>
}

export function useFormSubmission(): UseFormSubmissionReturn {
  const createJournal = useCreateJournal()
  const createTask = useCreateTask()
  const createMovie = useCreateMovie()
  const createBook = useCreateBook()
  const createArtist = useCreateArtist()
  const createLabel = useCreateLabel()
  const createRelease = useCreateRelease()
  // Derived loading state from any mutation
  const isLoading =
    createJournal.isPending ||
    createTask.isPending ||
    createMovie.isPending ||
    createBook.isPending ||
    createArtist.isPending ||
    createLabel.isPending ||
    createRelease.isPending

  const submitJournalOrTask = useCallback(
    async (
      draft: UnifiedDraftValues,
      effectiveType: CaptureEntryType | null,
      selectedPeople: PersonRef[],
      analysis: JournalAnalysis | TaskAnalysis | null,
      callbacks: SubmissionCallbacks,
    ) => {
      // Validate that there's at least title, content, or checklist items
      const hasContent =
        draft.content.trim().length > 0 ||
        draft.title.trim().length > 0 ||
        (draft.checklistItems && draft.checklistItems.length > 0)

      if (!hasContent) {
        throw new Error('Please write something first')
      }

      const finalType: CaptureEntryType = effectiveType || 'journal'
      const finalTitle =
        draft.title.trim() || analysis?.title || 'Untitled Entry'

      const handleSuccess = () => {
        // People are already linked by the create operation the mutation calls
        callbacks.onResetAll?.()
        callbacks.onSuccess?.()
      }

      const handleError = (err: Error) => {
        throw new Error(err.message || 'Failed to save entry')
      }

      const dueDate = draft.dueDate ? new Date(draft.dueDate) : undefined

      if (finalType === 'journal') {
        createJournal.mutate(
          {
            title: finalTitle,
            content: draft.content.trim(),
            projectId: draft.selectedProjectId ?? null,
            topicIds: draft.selectedTopicIds ?? [],
            peopleIds: selectedPeople.map((p) => p.id),
          },
          {
            onSuccess: (createdEntry) => {
              // Don't need to link people separately since they're created with the entry
              callbacks.onResetAll?.()
              callbacks.onSuccess?.()
            },
            onError: handleError,
          },
        )
      } else if (finalType === 'task') {
        createTask.mutate(
          {
            // `category`, `is_recurring` and `recurrence` are gone: all three were passed here and
            // read by nothing, so they never reached a document (#392).
            title: finalTitle,
            description: draft.content.trim() || null,
            dueDate: dueDate ? format(dueDate, 'yyyy-MM-dd') : null,
            projectId: draft.selectedProjectId ?? null,
            topicIds: draft.selectedTopicIds ?? [],
            peopleIds: selectedPeople.map((p) => p.id),
            status: 'todo',
            important: draft.important,
            checklist: draft.checklistItems ?? null,
          },
          {
            onSuccess: handleSuccess,
            onError: handleError,
          },
        )
      }
    },
    [createJournal, createTask],
  )

  const submitMedia = useCallback(
    async (
      aiDetectedType: MediaKind,
      analysis: MediaAnalysis,
      enrichedData: EnrichedMediaResult | null,
      editedMediaValues: MediaEditValues | undefined,
      callbacks: SubmissionCallbacks,
    ) => {
      const effectiveMediaType = (enrichedData?.type ??
        aiDetectedType) as MediaKind
      const mediaData = buildMediaCreateData(
        effectiveMediaType,
        analysis,
        enrichedData,
        editedMediaValues ?? undefined,
      )

      const handleSuccess = () => {
        callbacks.onResetAll?.()
        callbacks.onSuccess?.()
      }

      const handleError = (err: Error) => {
        throw new Error(err.message || 'Failed to save to library')
      }

      switch (mediaData.type) {
        case 'movie':
          createMovie.mutate(mediaData.data, {
            onSuccess: handleSuccess,
            onError: handleError,
          })
          break
        case 'book':
          createBook.mutate(mediaData.data, {
            onSuccess: handleSuccess,
            onError: handleError,
          })
          break
        case 'artist':
          createArtist.mutate(mediaData.data, {
            onSuccess: handleSuccess,
            onError: handleError,
          })
          break
        case 'label':
          createLabel.mutate(mediaData.data, {
            onSuccess: handleSuccess,
            onError: handleError,
          })
          break
        case 'release':
          createRelease.mutate(mediaData.data, {
            onSuccess: handleSuccess,
            onError: handleError,
          })
          break
      }
    },
    [createMovie, createBook, createArtist, createLabel, createRelease],
  )

  const submitManualMedia = useCallback(
    async (
      dismissedMediaType: MediaKind,
      content: string,
      editedMediaValues: MediaEditValues | undefined,
      callbacks: SubmissionCallbacks,
    ) => {
      if (!content.trim()) {
        throw new Error('Please write something first')
      }

      // Create a minimal analysis object from manual entry
      let manualAnalysis: MediaAnalysis
      const titleOrName =
        editedMediaValues?.title ||
        editedMediaValues?.name ||
        content.trim().split('\n')[0] ||
        'Untitled'

      if (dismissedMediaType === 'movie') {
        manualAnalysis = {
          type: 'movie',
          title: titleOrName,
          genres: editedMediaValues?.genres || [],
          year: editedMediaValues?.year || undefined,
          director: editedMediaValues?.director || undefined,
          status: 'want_to_watch',
        } as MediaAnalysis
      } else if (dismissedMediaType === 'book') {
        manualAnalysis = {
          type: 'book',
          title: titleOrName,
          topics: editedMediaValues?.genres || [],
          year: editedMediaValues?.year || undefined,
          author: editedMediaValues?.author || undefined,
          status: 'want_to_read',
        } as MediaAnalysis
      } else if (dismissedMediaType === 'artist') {
        manualAnalysis = {
          type: 'artist',
          name: titleOrName,
          genres: editedMediaValues?.genres || [],
        } as MediaAnalysis
      } else if (dismissedMediaType === 'label') {
        manualAnalysis = {
          type: 'label',
          name: titleOrName,
          genres: editedMediaValues?.genres || [],
        } as MediaAnalysis
      } else if (dismissedMediaType === 'release') {
        manualAnalysis = {
          type: 'release',
          title: titleOrName,
          genres: editedMediaValues?.genres || [],
          year: editedMediaValues?.year || undefined,
          artistName: undefined,
        } as MediaAnalysis
      } else {
        throw new Error(`Unsupported media type: ${dismissedMediaType}`)
      }

      const mediaData = buildMediaCreateData(
        dismissedMediaType,
        manualAnalysis,
        null, // No enriched data
        editedMediaValues,
      )

      const handleSuccess = () => {
        callbacks.onResetAll?.()
        callbacks.onSuccess?.()
      }

      const handleError = (err: Error) => {
        throw new Error(err.message || 'Failed to save to library')
      }

      switch (mediaData.type) {
        case 'movie':
          createMovie.mutate(mediaData.data, {
            onSuccess: handleSuccess,
            onError: handleError,
          })
          break
        case 'book':
          createBook.mutate(mediaData.data, {
            onSuccess: handleSuccess,
            onError: handleError,
          })
          break
        case 'artist':
          createArtist.mutate(mediaData.data, {
            onSuccess: handleSuccess,
            onError: handleError,
          })
          break
        case 'label':
          createLabel.mutate(mediaData.data, {
            onSuccess: handleSuccess,
            onError: handleError,
          })
          break
        case 'release':
          createRelease.mutate(mediaData.data, {
            onSuccess: handleSuccess,
            onError: handleError,
          })
          break
      }
    },
    [createMovie, createBook, createArtist, createLabel, createRelease],
  )

  return {
    isLoading,
    submitJournalOrTask,
    submitMedia,
    submitManualMedia,
  }
}
